import { ArrowDown, ArrowLeft } from '@tencent/odc-iconpack';
import { Button, Empty, MenuList } from 'odc-shared/components';
import OdcInput from 'odc-shared/components/Input/odcInput';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import ArticlesCard from '@src/components/ArticlesCard';
import { getHelpCenterT } from '@src/i18n';
import { buildLanguageQuery } from '@src/pages/supportCenter/constants/language';
import { getArticlePath } from '@src/utils/helpCenter';

import { PlatformCategory, SupportSearchPaginationState } from '../hooks';

interface SupportSearchProps {
  onBack: () => void;
  keyword: string;
  selectedCategory: PlatformCategory;
  searchState: SupportSearchPaginationState;
  // 当前关键词的第一页是否还没搜完（含防抖期）：用来区分"还没搜"与"搜过没结果"，避免先闪空态
  searchPending: boolean;
  onKeywordChange: (keyword: string) => void;
  onCategoryChange: (category: PlatformCategory) => void;
  onLoadMore: (platform: PlatformCategory) => void;
  /**
   * 当前搜索语言（只读）
   *
   * 结果是按发起端的语言取的（内页 View more 带 language，首页 Banner 发起＝英文）。
   * 本页**不提供语言切换入口**（技术方案 §3.6 已按此定稿）：本页是首页路由下的一个视图，
   * 换语言要回帮助中心内页（有语言下拉，且切换即重搜）。这里只用它拼文章链接的 language 参数。
   */
  language: string;
}

function SupportSearch({
  onBack,
  keyword,
  selectedCategory,
  searchState,
  searchPending,
  onKeywordChange,
  onCategoryChange,
  onLoadMore,
  language,
}: SupportSearchProps) {
  // 详情页链接上的语言参数：英文时为空串，保持与改造前一致
  const langQuery = useMemo(() => buildLanguageQuery(language), [language]);

  /**
   * 本页文案跟随**本页语言**（发起端语言），不读全局 i18next
   *
   * 全局 i18next 记录的是"上次访问帮助中心内页时的语言"：内页中文 → Back 回首页 →
   * 首页 Banner 进结果页（英文）时，若用全局语言就会出现"结果英文、按钮却是'返回首页'"的混排。
   */
  const t = useMemo(() => getHelpCenterT(language), [language]);
  // 当前选中平台的状态
  const currentPlatformState = useMemo(() => searchState[selectedCategory], [searchState, selectedCategory]);

  // 菜单数据 - 使用各平台的 totalCount
  const menuData = useMemo(() => {
    const allCount = searchState.all.totalCount;
    // 计数只在"确实还没有数字"时省略（首次搜索仍在途）：
    // 重新搜索时 hook 保留了上一次的计数，数字不消失、不闪（P6）
    const showAllCount = !searchPending || allCount > 0;
    const result: { id: PlatformCategory; label: string }[] = [{
      id: 'all',
      label: showAllCount ? `${t('All results')}（${allCount}）` : t('All results'),
    }];
    // 只有当平台有结果时才显示
    if (searchState.advertiser.totalCount > 0) {
      result.push({
        id: 'advertiser',
        label: `User growth（${searchState.advertiser.totalCount}）`,
      });
    }
    if (searchState.publisher.totalCount > 0) {
      result.push({
        id: 'publisher',
        label: `Monetization（${searchState.publisher.totalCount}）`,
      });
    }
    return result;
  }, [searchPending, searchState.all.totalCount, searchState.publisher.totalCount, searchState.advertiser.totalCount]);

  // 处理搜索输入变化
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onKeywordChange(e.target.value);
  }, [onKeywordChange]);

  // 处理分类选择
  const handleCategoryChange = useCallback((categoryId: string | number | undefined) => {
    if (typeof categoryId === 'string') {
      onCategoryChange(categoryId as PlatformCategory);
    }
  }, [onCategoryChange]);

  // 处理加载更多
  const handleLoadMore = useCallback(() => {
    onLoadMore(selectedCategory);
  }, [onLoadMore, selectedCategory]);

  // 「可寻址的结果页」：URL 里带 allResults=true（内页 View more 与首页原地切视图都会写）
  // 只有可寻址时才回写 URL：否则会往一个没有 allResults 的地址上塞 keyword，刷新落到半截页面
  const [urlAddressable] = useState(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('allResults') === 'true';
  });

  /**
   * 地址栏与页面状态保持同步（仅可寻址的结果页）
   *
   * 进入时的 keyword / language 由容器读一次（supportHome/index.tsx），这里负责**回写**：
   * 用户改关键词后把新值 replaceState 进 URL —— 不新增历史记录，同时让分享出去的链接、
   * 刷新都能还原同一页（同一语言、同一关键词）。
   *
   * language 本页不可切换，回写只是把它原样留在地址栏里（内页 View more 写进来的那份），
   * 保证刷新 / 分享后结果语言不变；首页原地切视图那种无 allResults 的地址则完全不写。
   *
   * 之前这里是"进页面就把 keyword / language 从地址栏删掉"，代价是分享链接在别人机器上
   * 会退回英文、关键词也丢，已改为回写。
   */
  useEffect(() => {
    if (!urlAddressable) return;
    const searchParams = new URLSearchParams(window.location.search);
    if ((searchParams.get('keyword') ?? '') === keyword && (searchParams.get('language') ?? '') === language) {
      return;
    }

    if (keyword) {
      searchParams.set('keyword', keyword);
    } else {
      searchParams.delete('keyword');
    }
    if (language) {
      searchParams.set('language', language);
    } else {
      searchParams.delete('language');
    }
    const query = searchParams.toString();
    window.history.replaceState(null, '', query ? `${window.location.pathname}?${query}` : window.location.pathname);
  }, [urlAddressable, keyword, language]);

  return (
    <div className="odc-page-search-results">
      <div className="search-results-category">
        <MenuList
          data={menuData}
          value={selectedCategory}
          onChange={handleCategoryChange}
          useHoverFill={false}
        />
      </div>
      <div className="search-results-content">
        <div className="search-results-content-inner">
          <div className="odc-page-back" onClick={onBack}>
            <ArrowLeft className="!mr-1r" />{t('Back to home')}
          </div>
          <div className="search-results-toolbar">
            <OdcInput
              search
              placeholder={t('Search by topic or keyword')}
              value={keyword}
              onChange={handleSearchChange}
              className="!my-6r"
            />
          </div>
          <div className="search-results-items">
            <div className="!gap-4r flex flex-col">
              {currentPlatformState.loading ? (
                // 首次加载中
                <div className="flex justify-center items-center py-8">
                  <span className="text-gray-500">{t('Loading...')}</span>
                </div>
              ) : currentPlatformState.results.length > 0 ? (
                // 显示搜索结果
                <>
                  {currentPlatformState.results.map((post, index) => (
                    <ArticlesCard
                      key={post.id || index}
                      title={post.title || t('Untitled')}
                      url={`/supportcenter?pid=${post.id}&platform=${post.platform}${langQuery}`}
                      content={post.content || t('No description available')}
                      // 平台名是品牌词，不翻译（与帮助中心内页的平台下拉一致）
                      category={post.platform === 'publisher' ? 'Monetization' : 'User growth'}
                      path={getArticlePath(post.catalogs?.[0]?.path || [])}
                    />
                  ))}
                  {/* 加载更多按钮 */}
                  {currentPlatformState.hasMore && currentPlatformState.loadingMore && (
                    <div className="flex justify-center items-center py-8">
                      <span className="text-gray-500">{t('Loading...')}</span>
                    </div>
                  )}
                  {currentPlatformState.hasMore && !currentPlatformState.loadingMore && (
                    <div className="load-more-wrapper">
                      <Button
                        onClick={handleLoadMore}
                        disabled={currentPlatformState.loadingMore}
                        size="sm"
                      >
                        {t('View more')}
                        <ArrowDown className="!ml-1r text-base" />
                      </Button>
                    </div>
                  )}
                </>
              ) : searchPending ? (
                // 第一页还没搜完：宁可是加载态，也不要先闪一下"没找到匹配结果"
                <div className="flex justify-center items-center py-8">
                  <span className="text-gray-500">{t('Loading...')}</span>
                </div>
              ) : keyword ? (
                // 搜索无结果
                <Empty
                  size="lg"
                  emptyTip={t('No quick matches found')}
                  emptyDesc={t('Try checking for typos or search anyway')}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SupportSearch;
